import * as React from 'react';
import {useDispatch} from 'react-redux';

import {Routing} from 'nutshell-core/routing';

import {CloseButton} from 'shells/button/close-button/close-button';
import {ConfirmationDialog} from 'shells/confirmation-dialog/confirmation-dialog';
import {ErrorState} from 'shells/error-state/error-state';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {Modal} from 'shells/modal/modal';
import {QuoteOverviewHeader} from 'shells/sales-documents/quote-overview-header';
import {SalesDocumentModalFooter} from 'shells/sales-documents/sales-document-modal-footer';
import {StatusSection} from 'shells/sales-documents/status/status-section';

import type {QuoteDisplayStatus} from 'nutshell-graphql-types';

import {refetchForEvent} from '../../../refetch-helpers';

import {useDismissibleUserSetting} from '../../../../../util/hooks/use-dismissible-user-setting';
import {EmailModalWrapper} from '../../../../core-entity/components/email-modal-wrapper';
import {INVOICES} from '../../../../reports/report-constants';
import type {ReportKey, ChartReportParams} from '../../../../reports/report-types';
import {requestLeadsReportData} from '../../../../reports/reports-actions';
import {
    getReportFiltersFromUrlParams,
    getReportParamsFromUrlParams,
} from '../../../../setup/sales-documents/shared/helper';

import {
    downloadQuote,
    getQuoteTimes,
    QuoteStatusOnboarding,
    handleSalesDocumentChange,
} from '../../shared';
import {PEANUT_TARGET, HAS_DISMISSED_STATUS_FOLLOWUP} from '../../shared/consts';
import {
    useUpdateSalesDocumentStatus,
    useDuplicateSalesDocument,
    useRenderPeanut,
} from '../../shared/graphql/hooks';

import {useGetInvoiceById, useGetInvoices} from '../graphql/hooks';

import {InvoiceWrapper, StatusConfirmationModal, InvoiceOverdueBanner, CompletionComment} from '.';
import type {ConfirmationVariant} from '.';

import styles from '../../shared/quotes.module.css';

type Props = {
    isOpen: boolean;
    onClose: () => void;
    invoiceId: string;
};

export function InvoiceModal(props: Props) {
    const {onClose} = props;

    const [hasActiveSection, setHasActiveSection] = React.useState<boolean>(false);
    const [showConfirmation, setShowConfirmation] = React.useState<boolean>(false);
    const [confirmationVariant, setConfirmationVariant] =
        React.useState<ConfirmationVariant | null>(null);
    const [peanutTarget, setPeanutTarget] = React.useState<HTMLElement | null>(null);
    const [isStatusFollowupOpen, setIsStatusFollowupOpen] = React.useState<boolean>(false);
    const [isEmailModalBeingOpened, setIsEmailModalBeingOpened] = React.useState<boolean>(false);

    const refetchLoading = React.useRef<boolean>(false);
    const peanutTargetRef = React.useCallback((node: HTMLElement | null) => {
        setPeanutTarget(node);
    }, []);

    const {invoice, isLoading, error} = useGetInvoiceById(props.invoiceId);

    const invoiceLeadId = invoice && invoice.lead ? invoice.lead.id : '';

    const {refetch} = useGetInvoices(invoiceLeadId, isLoading || !invoiceLeadId);

    const {updateSalesDocumentStatus} = useUpdateSalesDocumentStatus();
    const {duplicateSalesDocument} = useDuplicateSalesDocument();

    const {shouldShow: shouldShowStatusFollowup, onDismiss: onDismissStatusFollowup} =
        useDismissibleUserSetting(HAS_DISMISSED_STATUS_FOLLOWUP);

    const instanceId = NutshellFrontendSettings.instanceId;
    const {rerender: rerenderPeanut} = useRenderPeanut(instanceId, invoice, peanutTarget);

    const handleDuplicateInvoice = () => {
        duplicateSalesDocument({
            salesDocument: props.invoiceId,
        }).then((response) => {
            const newInvoice =
                response &&
                response.data &&
                response.data.salesDocumentDuplicate &&
                response.data.salesDocumentDuplicate.salesDocument;
            if (newInvoice) {
                handleSalesDocumentChange(newInvoice.modalUrl, refetchLoading, refetch);
            }
        });
    };

    const handleDownloadInvoice = (slug: string, scriptRef: HTMLElement, anchor: string) => {
        if (!instanceId) {
            return;
        }

        downloadQuote(instanceId, slug, scriptRef, anchor);
    };

    const handleTriggerRefetch = React.useCallback(() => {
        if (NutClientConfig.hasLeadPageRedesignM3FF) {
            refetchForEvent('salesDocumentStatusChange');
        } else {
            $(window).trigger(Lead.events.LeadBackboneModelDidUpdate);
            Timeline.refreshTimeline();
        }
    }, []);

    const handleUpdateStatus = React.useCallback(
        (status: QuoteDisplayStatus) => {
            if (status === 'ACCEPTED' || status === 'REVOKED') {
                setConfirmationVariant(status === 'ACCEPTED' ? 'paid' : 'void');

                return;
            }

            updateSalesDocumentStatus({
                id: props.invoiceId,
                status,
            }).then(() => {
                if (status === 'ARCHIVED') {
                    refetch();
                }
                // Re-render peanut when the status is updated
                rerenderPeanut();

                handleTriggerRefetch();
            });
            setHasActiveSection(false);
        },
        [updateSalesDocumentStatus, props.invoiceId, rerenderPeanut, refetch, handleTriggerRefetch]
    );

    const handleToggleActiveSection = (isActive: boolean) => {
        setHasActiveSection(isActive);
    };

    const handleBeforeClose = React.useCallback(() => {
        if (hasActiveSection) {
            setShowConfirmation(true);

            return false;
        }

        return true;
    }, [hasActiveSection]);

    const handleClose = () => {
        // Refetch report data when modal is closed so report reflects changes without a page refresh
        fetchReportData(INVOICES, reportParams, reportFilters);

        if (hasActiveSection) {
            setShowConfirmation(true);
        } else {
            props.onClose();
        }
    };

    const handleConfirmClose = () => {
        setShowConfirmation(false);
        setHasActiveSection(false);
        props.onClose();
    };

    const handleCancelConfirmation = () => {
        setShowConfirmation(false);
    };

    const handleSendActionMenuClose = () => {
        if (shouldShowStatusFollowup) {
            setIsStatusFollowupOpen(true);
        }
    };

    const handleStatusConfirmationSubmit = React.useCallback(
        (comment: string, status: QuoteDisplayStatus) => {
            updateSalesDocumentStatus({
                id: props.invoiceId,
                status: status,
                comment,
            }).then(() => {
                // Re-render peanut when the status is updated
                rerenderPeanut();

                handleTriggerRefetch();
            });
            setHasActiveSection(false);
        },
        [updateSalesDocumentStatus, props.invoiceId, rerenderPeanut, handleTriggerRefetch]
    );

    const handleStatusConfirmationClose = React.useCallback(() => {
        setConfirmationVariant(null);
    }, []);

    const handleSendViaEmail = React.useCallback(() => {
        setIsEmailModalBeingOpened(true);
    }, []);

    const handlePostEmailModalOpen = React.useCallback(() => {
        setIsEmailModalBeingOpened(false);
        onClose();
    }, [onClose]);

    const urlParamString = window.location.search.slice(1); // Remove "?"
    const urlParams = Routing.deparam(urlParamString);

    const reportFilters = getReportFiltersFromUrlParams(urlParams);
    const reportParams = getReportParamsFromUrlParams(urlParams);

    const dispatch = useDispatch();

    const fetchReportData = React.useCallback(
        (reportKeyForFetch: ReportKey, params: ChartReportParams, filters: Array<any>) => {
            dispatch(
                requestLeadsReportData({
                    reportKey: reportKeyForFetch,
                    reportParams: params,
                    filters,
                })
            );
        },
        [dispatch]
    );

    return (
        <>
            <Modal
                isOpen={props.isOpen}
                onClose={handleClose}
                onBeforeClose={handleBeforeClose}
                // @ts-expect-error
                maxHeight='90vh'
                minWidth={800}
                isWide={true}
                noPadding={true}
                closeButton={false}
                visibleOverflow={true}
            >
                {isLoading && (
                    <div className='absolutely-center-and-fill'>
                        <LoadingPage />
                    </div>
                )}
                {!isLoading && (error || !invoice) && (
                    <div className='absolutely-center-and-fill'>
                        <ErrorState errorMessage={error} />
                    </div>
                )}
                {!isLoading && !error && invoice && (
                    <>
                        <div className={styles['close-button-container']}>
                            <CloseButton onClick={handleClose} size={16} />
                        </div>
                        <QuoteOverviewHeader
                            quote={invoice}
                            handleQuoteDownload={handleDownloadInvoice}
                            handleUpdateStatus={handleUpdateStatus}
                            handleDuplicateQuote={handleDuplicateInvoice}
                            handleSendActionMenuClose={handleSendActionMenuClose}
                            handleSendEmail={handleSendViaEmail}
                        />
                        <div className={styles['quote-contents-container']}>
                            <div
                                className={invoice.displayStatus === 'DRAFT' ? 'border-bottom' : ''}
                            >
                                <div className='pad-16 flex justify-center'>
                                    <StatusSection
                                        quoteStatus={invoice.displayStatus}
                                        handleUpdateStatus={handleUpdateStatus}
                                        featureGated={
                                            !NutClientConfig.hasQuotePlan &&
                                            invoice.displayStatus === 'DRAFT'
                                        }
                                        quoteTimes={getQuoteTimes(invoice)}
                                        type='Invoice'
                                    />
                                </div>
                                {invoice.overdueDuration && (
                                    <InvoiceOverdueBanner
                                        overdueDuration={invoice.overdueDuration}
                                    />
                                )}
                                {invoice.completionComment && (
                                    <CompletionComment
                                        completionComment={invoice.completionComment}
                                        displayStatus={invoice.displayStatus}
                                    />
                                )}
                            </div>
                            {invoice.displayStatus === 'DRAFT' ? (
                                <InvoiceWrapper
                                    invoice={invoice}
                                    isTemplate={false}
                                    toggleHasActiveSection={handleToggleActiveSection}
                                />
                            ) : (
                                <div
                                    id={PEANUT_TARGET}
                                    ref={peanutTargetRef}
                                    className={styles['peanut-embed']}
                                />
                            )}
                        </div>
                        <SalesDocumentModalFooter statusLog={invoice.statusLog} />
                    </>
                )}
            </Modal>
            {confirmationVariant && invoice && (
                <StatusConfirmationModal
                    variant={confirmationVariant}
                    onClose={handleStatusConfirmationClose}
                    onSubmit={handleStatusConfirmationSubmit}
                    currentStatus={invoice.displayStatus}
                />
            )}
            {props.isOpen && showConfirmation && (
                <ConfirmationDialog
                    title='Unsaved Changes'
                    message='Are you sure you want to close this invoice without saving?'
                    onConfirm={handleConfirmClose}
                    onCancel={handleCancelConfirmation}
                    // @ts-expect-error
                    isOpen={showConfirmation}
                />
            )}
            {shouldShowStatusFollowup && onDismissStatusFollowup && (
                <QuoteStatusOnboarding
                    isStatusFollowupOpen={isStatusFollowupOpen}
                    setIsStatusFollowupOpen={setIsStatusFollowupOpen}
                    onDismissStatusFollowup={onDismissStatusFollowup}
                    handleUpdateStatus={handleUpdateStatus}
                    type='Invoice'
                />
            )}
            {isEmailModalBeingOpened && invoiceLeadId && (
                <EmailModalWrapper
                    entityId={invoiceLeadId}
                    postModalOpen={handlePostEmailModalOpen}
                    originSalesDocumentType='Invoice'
                />
            )}
        </>
    );
}
